import React, { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import {
  AdminLogin,
  InvestorForm,
  NotFound,
  NotificationDetails,
  Notifications,
} from "./screens";
import { ProtectedRoute, ScrollToTop } from "components";
import UserDetails from "./components/admin/userDetails";
import UpdateStore from "./components/admin/UpdateStore";
import Wallet from "./screens/UserDashboard/Wallet";

import {
  AddUpdateBlog,
  AddUpdateStore,
  AddUser,
  AdminDashboard,
  Dividend,
  Stores,
  DividendHistory,
  Transactions,
  Users,
  AdminTransactions,
  DonationShareTransfer,
  DonationShareHistory,
  Blogs,
  AdminWithDrawl,
  WithDrawlRequest,
  AdminInvest,
  AddUpdateUser,
  AdminUsers,
  SubAdminWithDrawl,
  UserDocuments,
  AdminDocuments,
  AdminSendNotification,
  NotificationsHistory,
  AnnualProgress,
  SharePriceHistory,
  DonationTokenHistory,
  DonationTokenTransfer,
  TransactionsOffer,
  Offers,
  AddUpdateOffer,
  TrackingAnalytics,
  WorldCupBonus,
  ShareholderRegistry,
  Java300Champions,
  ShareholderDetail,
  MarketplaceManagement,
} from "screens/Admin";

import { useDispatch, useSelector } from "react-redux";

import Language from "./components/Language";
import {
  setAdminNotifications,
  setTotalUnseenAdminNotifications,
} from "redux/slices/adminSlice";

import { Suspense } from "react";
import { io } from "socket.io-client";
import Logout from "screens/Logout";
import Referral from "screens/Admin/Referral";
import ShareManagement from "screens/Admin/ShareManagement";
import Popup from "screens/Admin/Popup";
import CreatePopup from "screens/Admin/CreatePopup";
import EditPopup from "screens/Admin/EditPopup";
import ViewPopup from "screens/Admin/ViewPopup";




import AddAnnualProgress from "screens/Admin/AddAnnualProgress";
import ManageTickets from "screens/Admin/ManageTickets";
import ManageAllTickets from "screens/Admin/ManageAllTickets";
import TicketDetails from "screens/Admin/TicketDetails";
import Beneficiary from "screens/UserDashboard/Beneficiary";
import { LEGAL_COMPLIANCE_ADMIN_TYPE, Local_URL } from "assets/constants";

const socket = io(Local_URL, {
  autoConnect: false,
  transports: ["websocket", "polling"],
  reconnection: true,
  reconnectionAttempts: 20,
  reconnectionDelay: 800,
  timeout: 12000,
  withCredentials: true,
});

const App = () => {
  const dispatch = useDispatch();
  const { adminUser } = useSelector((store) => store.admin);

  useEffect(() => {
    const resizeObserverMessage =
      "ResizeObserver loop completed with undelivered notifications";

    const handleResizeObserverError = (event) => {
      const message =
        event?.message ||
        event?.error?.message ||
        (typeof event?.reason === "string" ? event.reason : "");

      if (message.includes(resizeObserverMessage)) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
      }
    };

    window.addEventListener("error", handleResizeObserverError, true);
    window.addEventListener("unhandledrejection", handleResizeObserverError, true);
    return () => {
      window.removeEventListener("error", handleResizeObserverError, true);
      window.removeEventListener(
        "unhandledrejection",
        handleResizeObserverError,
        true
      );
    };
  }, []);

  useEffect(() => {
    const handleNotificationAdmin = (data) => {
      if (adminUser?.id === data?.adminId) {
        dispatch(setAdminNotifications(data?.notifications));
        dispatch(setTotalUnseenAdminNotifications(data?.unSeenNotifications));
      }
    };

    socket.on("notification-admin", handleNotificationAdmin);
    return () => {
      socket.off("notification-admin", handleNotificationAdmin);
    };
  }, [adminUser, dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("x-auth-token");
    if (!token) {
      socket.disconnect();
      return undefined;
    }

    const authenticate = () => socket.emit("support:authenticate", { token }, (response) => {
      if (!response?.status || !response?.isAdmin) {
        console.error("Support socket admin authentication failed", response);
        return;
      }
      window.dispatchEvent(new CustomEvent("support-chat-resync"));
    });
    const forwardSupportEvent = (conversation) => {
      window.dispatchEvent(new CustomEvent("support-chat-updated", { detail: conversation }));
    };
    const forwardTypingEvent = (payload) => {
      window.dispatchEvent(new CustomEvent("support-chat-typing", { detail: payload }));
    };
    const sendTypingEvent = (event) => socket.emit("support:typing", event.detail);
    const events = [
      "support:conversation-created",
      "support:conversation-updated",
      "support:message-created",
      "support:status-updated",
      "support:read-updated",
      "support:feedback-submitted",
      "support:conversation-deleted",
    ];

    socket.on("connect", authenticate);
    socket.auth = { token };
    socket.on("support:typing", forwardTypingEvent);
    window.addEventListener("support-chat-typing-send", sendTypingEvent);
    events.forEach((event) => socket.on(event, forwardSupportEvent));
    if (socket.connected) authenticate();
    else socket.connect();
    return () => {
      socket.off("connect", authenticate);
      socket.off("support:typing", forwardTypingEvent);
      window.removeEventListener("support-chat-typing-send", sendTypingEvent);
      events.forEach((event) => socket.off(event, forwardSupportEvent));
    };
  }, [adminUser]);

  return (
    <>
      <ScrollToTop />
      <Suspense

      // fallback={<PageLoader />}
      >
        <Routes>
          <Route path="/" element={<Language />}>
            <Route path="/:lang">
              <Route index element={<AdminLogin />} />
              <Route index path="admin-login" element={<AdminLogin />} />
              <Route path="logout" element={<Logout />} />
              {/*NOTE:: Admin Private Routes here*/}
              <Route
                path="admin/"
                element={
                  <ProtectedRoute
                    allowedTypes={["owner", LEGAL_COMPLIANCE_ADMIN_TYPE]}
                  />
                }
              >
                <Route path="users-document" element={<UserDocuments />} />
                <Route path="users-document/:id" element={<AdminDocuments />} />
                <Route path="legal-compliance" element={<ShareholderRegistry />} />
                <Route path="legal-compliance/:id" element={<ShareholderDetail />} />

                <Route element={<ProtectedRoute allowedTypes={["owner"]} />}>
                  <Route path="java300-champions" element={<Java300Champions />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="marketplace-management" element={<MarketplaceManagement />} />
                  <Route path="users" element={<Users />} />
                  <Route path="beneficiary" element={<Beneficiary />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route
                    path="notification/:id"
                    element={<NotificationDetails />}
                  />
                  <Route path="annual-progress" element={<AnnualProgress />} />
                  <Route
                    path="annual-progress/add"
                    element={<AddAnnualProgress />}
                  />

                  <Route path="user-details/:id" element={<UserDetails />} />
                  <Route path="stores" element={<Stores />} />
                  <Route path="dividend" element={<Dividend />} />
                  <Route path="stores/add/" element={<AddUpdateStore />} />
                  <Route path="stores/add/:id" element={<AddUpdateStore />} />
                  <Route path="store/update/:id" element={<UpdateStore />} />
                  <Route path="users/add" element={<AddUser />} />
                  <Route path="users/add/:id" element={<AddUpdateUser />} />

                  <Route path="transactions" element={<Transactions />} />
                  <Route
                    path="admin-transactions"
                    element={<AdminTransactions />}
                  />
                  <Route
                    path="transactions-offers"
                    element={<TransactionsOffer />}
                  />
                  <Route path="offers" element={<Offers />} />

                  <Route path="offers/add/" element={<AddUpdateOffer />} />
                  <Route path="offers/add/:id" element={<AddUpdateOffer />} />

                  <Route path="dividend-history" element={<DividendHistory />} />
                  <Route path="wallet" element={<Wallet />} />
                  <Route path="investor" element={<InvestorForm />} />
                  <Route path="withdrawl" element={<AdminWithDrawl />} />
                  <Route path="admin-invest/:id" element={<AdminInvest />} />
                  <Route
                    path="send-notification"
                    element={<AdminSendNotification />}
                  />
                  <Route
                    path="share-management"
                    element={<ShareManagement />}
                  />
                  <Route
                    path="popup-management"
                    element={<Popup />}
                  />
                  <Route
                    path="create-popup"
                    element={<CreatePopup />}
                  />
                  <Route path="edit-popup/:id" element={<EditPopup />} />

                  <Route path="view-details-popup/:id" element={<ViewPopup />} />

                  <Route
                    path="notification-history"
                    element={<NotificationsHistory />}
                  />
                  <Route
                    path="share-price-history"
                    element={<SharePriceHistory />}
                  />
                  <Route
                    path="tracking-analytics"
                    element={<TrackingAnalytics />}
                  />
                  <Route path="world-cup-bonus" element={<WorldCupBonus />} />

                  <Route path="referral-history" element={<Referral />} />

                  <Route
                    path="withdrawl-request"
                    element={<WithDrawlRequest />}
                  />
                  <Route
                    path="share-transfer"
                    element={<DonationShareTransfer />}
                  />
                  <Route
                    path="token-transfer"
                    element={<DonationTokenTransfer />}
                  />
                  <Route
                    path="share-transfer-history"
                    element={<DonationShareHistory />}
                  />
                  <Route
                    path="token-transfer-history"
                    element={<DonationTokenHistory />}
                  />

                  <Route path="blog" element={<Blogs />} />
                  <Route path="blog/add/:id" element={<AddUpdateBlog />} />
                  <Route path="blog/add/" element={<AddUpdateBlog />} />

                  <Route path="tickets/" element={<ManageTickets />}>
                    <Route path="manage-all/" element={<ManageAllTickets />} />
                    <Route path=":ticketId/" element={<TicketDetails />} />
                  </Route>
                </Route>
              </Route>

              {/*NOTE:: Sub Admin Private Routes here*/}
              <Route path="sub-admin/" element={<ProtectedRoute isSubAdmin />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="blog" element={<Blogs />} />
                <Route path="blog/add/:id" element={<AddUpdateBlog />} />
                <Route path="blog/add/" element={<AddUpdateBlog />} />
                <Route path="stores" element={<Stores />} />
                <Route path="stores/add/" element={<AddUpdateStore />} />
                <Route path="stores/add/:id" element={<AddUpdateStore />} />
                <Route path="store/update/:id" element={<UpdateStore />} />
                <Route path="wallet" element={<Wallet />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="offers" element={<Offers />} />
                <Route path="offers/add/" element={<AddUpdateOffer />} />
                <Route path="offers/add/:id" element={<AddUpdateOffer />} />
                <Route path="withdrawl" element={<SubAdminWithDrawl />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default App;
